// Context key + page derivation from the current route.
//
// The chat drawer scopes each conversation to a context key derived from the
// URL, and tells the companion which page it's on so it offers the right tools.
// Kept in a plain .ts module (not the .tsx drawer) so it's unit-testable and
// doesn't trip React Fast Refresh's "components-only export" rule.

export function getContextKeyFromPath(
  pathname: string,
  search = '',
): { key: string; page: string } {
  if (pathname.startsWith('/backtester/editor')) {
    const match = pathname.match(/\/backtester\/editor\/(.+)/)
    const strategyId = match?.[1] ?? new URLSearchParams(search).get('strategy')
    return { key: strategyId ? `backtest:${strategyId}` : 'backtest', page: 'backtest' }
  }
  // stats-v2 workspace — same strategy-scoped key as the editor, but page
  // 'backtest-report' so the companion loads the report scope tools.
  if (pathname.startsWith('/backtester/strategy/') && pathname.endsWith('/stats-v2')) {
    const match = pathname.match(/\/backtester\/strategy\/([^/]+)\/stats-v2/)
    return { key: match ? `backtest:${match[1]}` : 'backtest', page: 'backtest-report' }
  }
  // Strategy results surfaces (variants + stats) share the editor's context key
  // for the same strategy id, so "Analyze with AI" from the results page
  // continues the same backtest conversation. `[^/]+` stops before any
  // trailing segment (e.g. `/stats`), so both routes resolve to the same key.
  if (pathname.startsWith('/backtester/strategy/')) {
    const match = pathname.match(/\/backtester\/strategy\/([^/]+)/)
    return { key: match ? `backtest:${match[1]}` : 'backtest', page: 'backtest' }
  }
  if (pathname.startsWith('/history')) return { key: 'history', page: 'history' }
  if (pathname.startsWith('/playbook')) return { key: 'playbook', page: 'playbook' }
  if (pathname.startsWith('/research')) {
    const match = pathname.match(/\/research\/([^/]+)/)
    return { key: match ? `research:${match[1]}` : 'research', page: 'research' }
  }
  if (pathname.startsWith('/dashboard')) return { key: 'dashboard', page: 'dashboard' }
  return { key: 'general', page: 'general' }
}
